import { createTheme, MantineTheme } from '@mantine/core'

import classes from '~/widgets/shared/theme.module.css'

// Widget-specific Mantine theme - separate from main app theme
export const widgetTheme = createTheme({
  primaryColor: 'dark',
  fontFamily: 'Figtree, Inter, sans-serif',

  // Custom colors for widgets
  colors: {
    primary: [
      '#F9F6ED',
      '#F5EEE0',
      '#E9D9BF',
      '#DBBF96',
      '#CDA371',
      '#C0884F',
      '#B27444',
      '#945C3A',
      '#945C3A',
      '#342016',
    ],
    dark: [
      '#f8f9fa',
      '#e9ecef',
      '#dee2e6',
      '#ced4da',
      '#adb5bd',
      '#6c757d',
      '#495057',
      '#343a40',
      '#212529',
      '#1d1f2c',
    ],
  },

  // Widget-specific component defaults
  components: {
    Button: {
      defaultProps: {
        // size: 'sm',
        variant: 'filled',
        color: 'dark.8',
        radius: 8,
      },
    },
    Text: {
      defaultProps: {
        size: 'sm',
      },
      styles: (_theme: MantineTheme, props: { variant?: string }) => {
        const variantMap: Record<string, Record<string, string | number>> = {
          // Figtree heading variants
          h1: {
            fontFamily: 'Figtree, sans-serif',
            fontSize: '56px',
            fontWeight: 600,
            lineHeight: '72px',
            letterSpacing: '-0.01em',
          },
          h2: {
            fontFamily: 'Figtree, sans-serif',
            fontSize: '48px',
            fontWeight: 600,
            lineHeight: '64px',
            letterSpacing: '-0.01em',
          },
          h3: {
            fontFamily: 'Figtree, sans-serif',
            fontSize: '40px',
            fontWeight: 600,
            lineHeight: '48px',
            letterSpacing: '-0.01em',
          },
          headline: {
            fontFamily: 'Figtree, sans-serif',
            fontSize: '32px',
            fontWeight: 600,
            lineHeight: '40px',
          },
          h4: {
            fontFamily: 'Figtree, sans-serif',
            fontSize: '28px',
            fontWeight: 600,
            lineHeight: '40px',
          },
          h5: {
            fontFamily: 'Figtree, sans-serif',
            fontSize: '20px',
            fontWeight: 600,
            lineHeight: '28px',
          },
          // Inter body text variants
          subtitle1: {
            fontFamily: 'Inter, sans-serif',
            fontSize: '18px',
            fontWeight: 600,
            lineHeight: '24px',
          },
          subtitle2: {
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '18px',
          },
          body1: {
            fontFamily: 'Inter, sans-serif',
            fontSize: '18px',
            fontWeight: 500,
            lineHeight: '24px',
          },
          body2: {
            fontFamily: 'Inter, sans-serif',
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '24px',
          },
          body3: {
            fontFamily: 'Inter, sans-serif',
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '24px',
          },
          button: {
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
            fontWeight: 600,
            lineHeight: '20px',
          },
          caption: {
            fontFamily: 'Inter, sans-serif',
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: '16px',
          },
        }
        return variantMap[props.variant as string]
          ? { root: variantMap[props.variant as string] }
          : {}
      },
    },
    TextInput: {
      defaultProps: {
        radius: 8,
      },
      classNames: {
        input: classes.textInputInput,
      },
      styles: {
        input: {
          backgroundColor: '#F9F6ED',
          border: '1px solid #E9D9BF',
        },
      },
    },
    Select: {
      defaultProps: {
        radius: 8,
      },
      classNames: {
        input: classes.selectInput,
        option: classes.selectOption,
      },
      styles: {
        input: {
          backgroundColor: '#F9F6ED',
          border: '1px solid #E9D9BF',
        },
        dropdown: {
          borderRadius: 8,
          border: '1px solid #E9D9BF',
        },
        option: {
          borderRadius: 4,
        },
      },
    },
    Card: {
      defaultProps: {
        radius: 8,
      },
    },
    Badge: {
      defaultProps: {
        radius: 6,
      },
    },
    Box: {
      defaultProps: {
        style: {
          fontFamily: 'Figtree, Inter, sans-serif',
        },
      },
    },
  },

  // Spacing and sizing for widgets
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
  },

  // Typography scale
  fontSizes: {
    xs: '12px',
    sm: '14px',
    md: '16px',
    lg: '18px',
    xl: '20px',
  },
})
